import api from './authService';

export const ragService = {
  // Cache simples em memória por sessão (TTL curto)
  _cache: {
    activeByAgent: new Map(),
    availableByAgent: new Map(),
    kbList: null,
    kbById: new Map(),
    docsByKb: new Map(),
    statsByKb: new Map(),
  },
  _setCache(map, key, data, ttlMs = 10000) {
    map.set(key, { data, expiresAt: Date.now() + ttlMs });
  },
  _getCache(map, key) {
    const entry = map.get(key);
    if (entry && entry.expiresAt > Date.now()) return entry.data;
    if (entry) map.delete(key);
    return null;
  },
  // ============================================
  // KNOWLEDGE BASES
  // ============================================

  // Get all knowledge bases
  getKnowledgeBases: async () => {
    try {
      // Cache de 30s para listagem geral
      const cache = ragService._cache.kbList;
      if (cache && cache.expiresAt > Date.now()) return cache.data;

      const response = await api.get('/rag/knowledge-bases');
      ragService._cache.kbList = { data: response.data, expiresAt: Date.now() + 30000 };
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get knowledge bases summary (leve)
  getKnowledgeBasesSummary: async (isActive = null) => {
    try {
      if (!ragService._cache.kbSummary) ragService._cache.kbSummary = new Map();
      const key = `sum:${isActive === null ? 'all' : isActive}`;
      const cached = ragService._cache.kbSummary.get(key);
      const now = Date.now();
      if (cached && cached.expiresAt > now && cached.data) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const params = isActive === null ? {} : { is_active: isActive };
      const inflight = api.get('/rag/knowledge-bases/summary', { params }).then(res => {
        ragService._cache.kbSummary.set(key, { data: res.data, expiresAt: now + 30000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = ragService._cache.kbSummary.get(key);
        if (c) ragService._cache.kbSummary.set(key, { ...c, inflight: null });
        throw err;
      });
      ragService._cache.kbSummary.set(key, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      throw error;
    }
  },

  // Get knowledge base by ID
  getKnowledgeBase: async (id) => {
    try {
      // Deduplicação + cache leve (15s)
      if (!ragService._cache.kbById) ragService._cache.kbById = new Map();
      const cache = ragService._cache.kbById.get(id);
      const now = Date.now();
      if (cache && cache.expiresAt > now && cache.data) return cache.data;
      if (cache?.inflight) return await cache.inflight;

      const inflight = api.get(`/rag/knowledge-bases/${id}`).then(res => {
        ragService._cache.kbById.set(id, { data: res.data, expiresAt: now + 15000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = ragService._cache.kbById.get(id);
        if (c) ragService._cache.kbById.set(id, { ...c, inflight: null });
        throw err;
      });
      ragService._cache.kbById.set(id, { data: null, expiresAt: 0, inflight });
      const response = await inflight;
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Create new knowledge base
  createKnowledgeBase: async (kbData) => {
    try {
      const response = await api.post('/rag/knowledge-bases', kbData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update knowledge base
  updateKnowledgeBase: async (id, kbData) => {
    try {
      const response = await api.put(`/rag/knowledge-bases/${id}`, kbData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete knowledge base (soft delete)
  deleteKnowledgeBase: async (id) => {
    try {
      const response = await api.delete(`/rag/knowledge-bases/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reactivate knowledge base
  reactivateKnowledgeBase: async (id) => {
    try {
      const response = await api.put(`/rag/knowledge-bases/${id}/reactivate`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get agents using a specific knowledge base
  getAgentsUsingKnowledgeBase: async (kbId) => {
    try {
      const response = await api.get(`/rag/knowledge-bases/${kbId}/agents`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get knowledge base statistics
  getKnowledgeBaseStats: async (id) => {
    try {
      const map = ragService._cache.statsByKb;
      const cached = map.get(id);
      const now = Date.now();
      if (cached && cached.expiresAt > now && cached.data) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const inflight = api.get(`/rag/knowledge-bases/${id}/stats`).then(res => {
        map.set(id, { data: res.data, expiresAt: now + 10000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = map.get(id);
        if (c) map.set(id, { ...c, inflight: null });
        throw err;
      });
      map.set(id, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // DOCUMENTS
  // ============================================

  // Get documents from knowledge base
  getDocuments: async (kbId) => {
    try {
      const map = ragService._cache.docsByKb;
      const cached = map.get(kbId);
      const now = Date.now();
      if (cached && cached.expiresAt > now && cached.data) return cached.data;
      if (cached?.inflight) return await cached.inflight;

      const inflight = api.get(`/rag/knowledge-bases/${kbId}/documents`).then(res => {
        map.set(kbId, { data: res.data, expiresAt: now + 10000, inflight: null });
        return res.data;
      }).catch(err => {
        const c = map.get(kbId);
        if (c) map.set(kbId, { ...c, inflight: null });
        throw err;
      });
      map.set(kbId, { data: null, expiresAt: 0, inflight });
      return await inflight;
    } catch (error) {
      throw error;
    }
  },

  // Upload document to knowledge base
  uploadDocument: async (kbId, file, chunkingConfig = null, onProgress = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Add chunking configuration if provided
      if (chunkingConfig) {
        formData.append('chunk_size', chunkingConfig.chunk_size || 1000);
        formData.append('chunk_overlap', chunkingConfig.chunk_overlap || 200);
        formData.append('chunker_type', chunkingConfig.chunker_type || 'recursive');
        // ✅ CORRIGIR nome do campo para match com backend
        if (chunkingConfig.separators) {
          formData.append('separators', JSON.stringify(chunkingConfig.separators));
        }
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      // Add progress callback if provided and is a function
      if (onProgress && typeof onProgress === 'function') {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        };
      }

      const response = await api.post(
        `/rag/knowledge-bases/${kbId}/documents/upload`,
        formData,
        config
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get document by ID
  getDocument: async (docId) => {
    try {
      const response = await api.get(`/rag/documents/${docId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reprocess document
  reprocessDocument: async (docId) => {
    try {
      const response = await api.post(`/rag/documents/${docId}/reprocess`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // CHUNKING CONFIGURATION
  // ============================================

  // Get chunking strategies
  getChunkingStrategies: async () => {
    try {
      const response = await api.get('/rag/chunking/strategies');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get chunking recommendation
  getChunkingRecommendation: async (fileInfo) => {
    try {
      const response = await api.post('/rag/chunking/recommend', fileInfo);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Check chunking health
  checkChunkingHealth: async () => {
    try {
      const response = await api.get('/rag/chunking/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Test chunking system
  testChunkingSystem: async () => {
    try {
      const response = await api.get('/rag/chunking/test');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // DOCUMENT PREVIEW
  // ============================================

  // Preview document chunks
  previewDocumentChunks: async (file, chunkingConfig = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('chunker_type', chunkingConfig.chunker_type || 'recursive');
      formData.append('chunk_size', chunkingConfig.chunk_size || 1000);
      formData.append('chunk_overlap', chunkingConfig.chunk_overlap || 200);
      formData.append('max_preview_chunks', chunkingConfig.max_preview_chunks || 10);

      if (chunkingConfig.separators) {
        formData.append('separators', JSON.stringify(chunkingConfig.separators));
      }

      if (chunkingConfig.semanticConfig) {
        formData.append('semantic_config', JSON.stringify(chunkingConfig.semanticConfig));
      }

      const response = await api.post('/rag/documents/preview', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete document
  deleteDocument: async (docId) => {
    try {
      const response = await api.delete(`/rag/documents/${docId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get document chunks
  getDocumentChunks: async (docId) => {
    try {
      const response = await api.get(`/rag/documents/${docId}/chunks`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // SEARCH
  // ============================================

  // Search in knowledge base
  searchKnowledgeBase: async (kbId, searchParams) => {
    try {
      const response = await api.post(`/rag/knowledge-bases/${kbId}/search`, searchParams);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // ============================================
  // AGENT KNOWLEDGE BASES MANAGEMENT
  // ============================================

  // Get knowledge bases available for a specific agent
  getAgentKnowledgeBases: async (agentId) => {
    try {
      const response = await api.get(`/rag/agents/${agentId}/knowledge-bases`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toggle knowledge base for a specific agent
  toggleAgentKnowledgeBase: async (agentId, toggleData) => {
    try {
      const response = await api.post(`/rag/agents/${agentId}/knowledge-bases/toggle`, toggleData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // 🚀 OTIMIZADO: Get only ACTIVE knowledge bases for agent (85% less data)
  getAgentActiveKnowledgeBases: async (agentId) => {
    try {
      // Cache de 10s para reduzir chamadas repetidas
      const cached = ragService._getCache(ragService._cache.activeByAgent, agentId);
      if (cached) return cached;

      const response = await api.get(`/rag/agents/${agentId}/active-knowledge-bases`);
      ragService._setCache(ragService._cache.activeByAgent, agentId, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching active knowledge bases:', error);
      throw error;
    }
  },

  // 📋 OTIMIZADO: Get AVAILABLE knowledge bases for selection dialog
  getAgentAvailableKnowledgeBases: async (agentId) => {
    try {
      // Cache de 10s - suficiente para navegação do diálogo
      const cached = ragService._getCache(ragService._cache.availableByAgent, agentId);
      if (cached) return cached;

      const response = await api.get(`/rag/agents/${agentId}/available-knowledge-bases`);
      ragService._setCache(ragService._cache.availableByAgent, agentId, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching available knowledge bases:', error);
      throw error;
    }
  },

  // ============================================
  // SYSTEM
  // ============================================

  // Clear cache
  clearCache: async () => {
    try {
      const response = await api.post('/rag/clear-cache');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get('/rag/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
}; 